import * as React from "react";
import {
  ArrowRightIcon,
  CaretLeftIcon,
  CheckIcon,
  LockSimpleIcon,
  MinusIcon,
  PaperPlaneTiltIcon,
  PauseIcon,
  SparkleIcon,
  WarningCircleIcon,
  XIcon,
} from "@phosphor-icons/react";

import {
  useWizard,
  type WizardApi,
  type WizardProposal,
  type WizardStepDef,
  type WizardStepStatus,
} from "../../hooks/use-wizard";
import { cn } from "../../lib/utils";
import { Badge } from "./badge";
import { Button } from "./button";
import { Input } from "./input";
import { Label } from "./label";
import { Spinner } from "./spinner";

const WizardContext = React.createContext<WizardApi | null>(null);

function useWizardContext(): WizardApi {
  const context = React.useContext(WizardContext);
  if (!context) {
    throw new Error("Wizard components must be used within <Wizard>.");
  }
  return context;
}

// --- root ----------------------------------------------------------------

interface WizardProps extends React.ComponentProps<"div"> {
  api: WizardApi;
}

function Wizard({ api, className, children, ...props }: WizardProps) {
  return (
    <WizardContext.Provider value={api}>
      <div
        data-slot="wizard"
        className={cn(
          "bg-card text-card-foreground flex min-h-0 w-full items-stretch overflow-hidden rounded-xl border",
          className,
        )}
        {...props}
      >
        {children}
      </div>
    </WizardContext.Provider>
  );
}

// --- rail ----------------------------------------------------------------

function statusMarker(
  status: WizardStepStatus,
  index: number,
  step: WizardStepDef,
) {
  const base =
    "flex size-6 shrink-0 items-center justify-center rounded-full border text-xs font-medium transition-colors";
  switch (status) {
    case "complete":
      return (
        <span
          className={cn(
            base,
            "bg-primary text-primary-foreground border-transparent",
          )}
        >
          <CheckIcon className="size-3.5" weight="bold" />
        </span>
      );
    case "active":
      return (
        <span className={cn(base, "border-foreground text-foreground")}>
          {step.icon ? <step.icon className="size-3.5" /> : index + 1}
        </span>
      );
    case "skipped":
      return (
        <span className={cn(base, "text-muted-foreground border-border")}>
          <MinusIcon className="size-3.5" />
        </span>
      );
    case "locked":
      return (
        <span className={cn(base, "text-muted-foreground/60 border-border")}>
          <LockSimpleIcon className="size-3.5" />
        </span>
      );
    default:
      return (
        <span className={cn(base, "text-muted-foreground border-border")}>
          {index + 1}
        </span>
      );
  }
}

function WizardRailItem({
  step,
  index,
}: {
  step: WizardStepDef;
  index: number;
}) {
  const api = useWizardContext();
  const status = api.statusOf(step.id);
  const reachable = status !== "locked";
  const proposals = api.agent.proposalsOf(step.id).length;
  const errors = api
    .issuesOf(step.id)
    .filter((i) => i.severity === "error").length;

  return (
    <button
      type="button"
      data-slot="wizard-rail-item"
      data-status={status}
      disabled={!reachable}
      onClick={() => reachable && void api.goTo(step.id)}
      className={cn(
        "group flex w-full items-start gap-3 rounded-md p-2 text-left transition-colors",
        "hover:bg-sidebar-accent disabled:pointer-events-none",
        status === "active" && "bg-sidebar-accent",
      )}
    >
      {statusMarker(status, index, step)}
      <span className="flex min-w-0 flex-1 flex-col gap-0.5 pt-0.5">
        <span className="flex items-center gap-1.5">
          <span
            className={cn(
              "truncate text-sm",
              status === "active"
                ? "text-foreground font-medium"
                : status === "locked"
                  ? "text-muted-foreground/70"
                  : "text-foreground/90",
            )}
          >
            {step.title}
          </span>
          {step.gate ? (
            <WarningCircleIcon
              className="text-muted-foreground size-3.5 shrink-0"
              weight="fill"
              aria-label="Confirmation required"
            />
          ) : null}
        </span>
        {step.description ? (
          <span className="text-muted-foreground line-clamp-1 text-xs">
            {step.description}
          </span>
        ) : null}
        {errors > 0 ? (
          <span className="text-destructive text-xs">
            {errors} {errors === 1 ? "issue" : "issues"}
          </span>
        ) : proposals > 0 ? (
          <span className="text-agent flex items-center gap-1 text-xs">
            <SparkleIcon className="size-3" weight="fill" />
            {proposals} {proposals === 1 ? "suggestion" : "suggestions"}
          </span>
        ) : null}
      </span>
    </button>
  );
}

interface WizardRailProps extends React.ComponentProps<"nav"> {
  orientation?: "vertical" | "horizontal";
}

function WizardRail({
  orientation = "vertical",
  className,
  ...props
}: WizardRailProps) {
  const api = useWizardContext();

  if (orientation === "horizontal") {
    return (
      <nav
        data-slot="wizard-rail"
        data-orientation="horizontal"
        aria-label="Steps"
        className={cn(
          "bg-sidebar flex w-full items-center gap-1 overflow-x-auto border-b p-2",
          className,
        )}
        {...props}
      >
        {api.steps.map((step, index) => {
          const status = api.statusOf(step.id);
          const reachable = status !== "locked";
          return (
            <button
              key={step.id}
              type="button"
              data-status={status}
              disabled={!reachable}
              onClick={() => reachable && void api.goTo(step.id)}
              className={cn(
                "flex shrink-0 items-center gap-2 rounded-md px-2 py-1 transition-colors",
                "hover:bg-sidebar-accent disabled:pointer-events-none",
                status === "active" && "bg-sidebar-accent",
              )}
            >
              {statusMarker(status, index, step)}
              <span
                className={cn(
                  "text-sm whitespace-nowrap",
                  status === "active"
                    ? "text-foreground font-medium"
                    : "text-muted-foreground",
                )}
              >
                {step.title}
              </span>
            </button>
          );
        })}
      </nav>
    );
  }

  return (
    <nav
      data-slot="wizard-rail"
      data-orientation="vertical"
      aria-label="Steps"
      className={cn(
        "bg-sidebar flex w-60 shrink-0 flex-col gap-0.5 overflow-y-auto border-r p-3",
        className,
      )}
      {...props}
    >
      {api.steps.map((step, index) => (
        <WizardRailItem key={step.id} step={step} index={index} />
      ))}
    </nav>
  );
}

// --- surface -------------------------------------------------------------

function WizardSurface({
  className,
  ...props
}: React.ComponentProps<"section">) {
  return (
    <section
      data-slot="wizard-surface"
      className={cn("flex min-w-0 flex-1 flex-col", className)}
      {...props}
    />
  );
}

function WizardHeader({
  className,
  children,
  ...props
}: React.ComponentProps<"header">) {
  const api = useWizardContext();
  const step = api.activeStep;
  const blockingIssues = api
    .issuesOf(step.id)
    .filter((i) => !i.field && i.source === "validation");

  return (
    <header
      data-slot="wizard-header"
      className={cn("flex flex-col gap-1 border-b px-5 py-4", className)}
      {...props}
    >
      <div className="flex items-center gap-2">
        <h3 className="text-foreground text-xl font-semibold">{step.title}</h3>
        {step.gate ? (
          <Badge variant="outline" className="gap-1">
            <WarningCircleIcon className="size-3" weight="fill" />
            Confirmation required
          </Badge>
        ) : null}
      </div>
      {step.description ? (
        <p className="text-muted-foreground text-sm font-light">
          {step.description}
        </p>
      ) : null}
      {children}
      {blockingIssues.map((issue) => (
        <p
          key={issue.id}
          className="text-destructive mt-1 flex items-center gap-1.5 text-sm"
        >
          <WarningCircleIcon className="size-4 shrink-0" weight="fill" />
          {issue.message}
        </p>
      ))}
    </header>
  );
}

interface WizardPanelProps extends React.ComponentProps<"div"> {
  stepId: string;
  /** Keep the panel mounted (hidden) when inactive, preserving its state. */
  keepMounted?: boolean;
}

function WizardPanel({
  stepId,
  keepMounted = false,
  className,
  ...props
}: WizardPanelProps) {
  const api = useWizardContext();
  const active = api.activeStepId === stepId;
  if (!active && !keepMounted) return null;

  return (
    <div
      data-slot="wizard-panel"
      data-state={active ? "active" : "inactive"}
      hidden={!active}
      className={cn(
        "flex flex-col gap-5",
        active &&
          "animate-in fade-in-0 slide-in-from-right-2 duration-200 ease-out",
        className,
      )}
      {...props}
    />
  );
}

// --- field with inline proposal -----------------------------------------

function formatProposedValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (Array.isArray(value)) return value.map(formatProposedValue).join(", ");
  return JSON.stringify(value);
}

function ProposalChip({
  proposal,
  field,
  sensitive,
  formatValue,
}: {
  proposal: WizardProposal;
  field: string;
  sensitive?: boolean;
  formatValue?: (value: unknown) => React.ReactNode;
}) {
  const api = useWizardContext();
  const { confidence, rationale, provenance } = proposal.meta;
  const rawValue = proposal.values[field];
  // Never render a sensitive proposed value (API keys, tokens) in clear text.
  const displayValue = formatValue
    ? formatValue(rawValue)
    : sensitive
      ? "••••••••"
      : formatProposedValue(rawValue);

  return (
    <div
      data-slot="wizard-proposal"
      className="border-agent-border bg-agent-subtle mt-0.5 flex flex-col gap-2 rounded-md border p-2.5 text-xs"
    >
      <div className="text-agent flex items-center gap-1.5">
        <SparkleIcon className="size-3.5" weight="fill" />
        <span className="font-medium">Suggested</span>
        {typeof confidence === "number" ? (
          <span className="text-muted-foreground font-normal">
            {Math.round(confidence * 100)}% confidence
          </span>
        ) : null}
      </div>
      <div className="text-foreground text-sm font-medium break-words">
        {displayValue}
      </div>
      {rationale ? <p className="text-muted-foreground">{rationale}</p> : null}
      {provenance && provenance.length > 0 ? (
        <div className="text-muted-foreground flex flex-wrap gap-1">
          {provenance.map((ref, i) =>
            ref.href ? (
              <a
                key={i}
                href={ref.href}
                className="hover:text-foreground underline underline-offset-2"
                title={ref.detail}
              >
                {ref.label}
              </a>
            ) : (
              <span
                key={i}
                title={ref.detail}
                className="underline underline-offset-2"
              >
                {ref.label}
              </span>
            ),
          )}
        </div>
      ) : null}
      <div className="flex gap-1.5">
        <Button
          type="button"
          size="sm"
          onClick={() => api.agent.acceptProposal(proposal.id)}
        >
          <CheckIcon weight="bold" />
          Accept
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() => api.agent.rejectProposal(proposal.id)}
        >
          Dismiss
        </Button>
      </div>
    </div>
  );
}

interface WizardFieldProps extends Omit<
  React.ComponentProps<"div">,
  "children"
> {
  /** Key in the wizard's values; used to match agent proposals. */
  name: string;
  label?: React.ReactNode;
  hint?: React.ReactNode;
  /** Mask the proposed value in the suggestion chip (API keys, tokens). */
  sensitive?: boolean;
  /** Custom renderer for the proposed value, overrides the default and `sensitive`. */
  formatValue?: (value: unknown) => React.ReactNode;
  children: React.ReactNode;
}

function WizardField({
  name,
  label,
  hint,
  sensitive,
  formatValue,
  className,
  children,
  ...props
}: WizardFieldProps) {
  const api = useWizardContext();
  const proposal = api.agent.proposalForField(api.activeStepId, name);
  const fieldIssues = api
    .issuesOf(api.activeStepId)
    .filter((i) => i.field === name);

  return (
    <div
      data-slot="wizard-field"
      className={cn("flex flex-col gap-1.5", className)}
      {...props}
    >
      {label ? <Label>{label}</Label> : null}
      {children}
      {hint && fieldIssues.length === 0 ? (
        <p className="text-muted-foreground text-xs">{hint}</p>
      ) : null}
      {fieldIssues.map((issue) => (
        <p key={issue.id} className="text-destructive text-xs">
          {issue.message}
        </p>
      ))}
      {proposal ? (
        <ProposalChip
          proposal={proposal}
          field={name}
          sensitive={sensitive}
          formatValue={formatValue}
        />
      ) : null}
    </div>
  );
}

// --- footer --------------------------------------------------------------

function WizardFooter({
  className,
  children,
  ...props
}: React.ComponentProps<"footer">) {
  const api = useWizardContext();
  const { activeStep, isLastStep, isBusy } = api;
  const suggested = api.agent.suggestedNextStepId === api.activeStepId;
  // While a gate confirmation is pending, the only valid actions are on the
  // confirmation card itself; lock the nav so Next can't re-enter the gate.
  const awaitingConfirm = api.confirmation !== null;
  const locked = isBusy || awaitingConfirm;

  const primaryLabel = isLastStep
    ? activeStep.gate
      ? "Confirm and finish"
      : "Finish"
    : activeStep.gate
      ? "Review and continue"
      : "Continue";

  return (
    <footer
      data-slot="wizard-footer"
      className={cn(
        "bg-card flex flex-col gap-2 border-t px-5 py-3",
        className,
      )}
      {...props}
    >
      <WizardConfirm />
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="ghost"
          onClick={api.back}
          disabled={!api.canGoBack || locked}
        >
          <CaretLeftIcon />
          Back
        </Button>
        {children}
        <div className="flex-1" />
        {api.canSkip ? (
          <Button
            type="button"
            variant="ghost"
            onClick={api.skip}
            disabled={locked}
          >
            Skip
          </Button>
        ) : null}
        <Button
          type="button"
          onClick={() => void api.next()}
          disabled={locked}
          className={cn(suggested && "ring-agent ring-2 ring-offset-1")}
        >
          {isBusy ? <Spinner size="sm" color="secondary" /> : null}
          {primaryLabel}
          {!isBusy && !activeStep.gate ? <ArrowRightIcon /> : null}
        </Button>
      </div>
    </footer>
  );
}

// --- assist (the agent panel) -------------------------------------------

function ThinkingLine({ active }: { active: boolean }) {
  return (
    <div className="h-0.5 w-full overflow-hidden" aria-hidden="true">
      {active ? (
        <div className="bg-agent animate-wizard-sweep h-full w-1/3 rounded-full" />
      ) : null}
    </div>
  );
}

/**
 * The gate confirmation, surfaced at the point of decision. Rendered by
 * WizardFooter by default so every layout (agent or not) enforces gates;
 * exported for custom layouts that place it elsewhere. Returns null when
 * there is nothing to confirm.
 */
function WizardConfirm({ className, ...props }: React.ComponentProps<"div">) {
  const api = useWizardContext();
  const confirmation = api.confirmation;
  if (!confirmation) return null;

  return (
    <div
      data-slot="wizard-confirm"
      className={cn(
        "bg-card flex flex-col gap-2 rounded-lg border p-3 shadow-sm",
        className,
      )}
      {...props}
    >
      <div className="text-muted-foreground flex items-center gap-1.5 text-xs font-medium">
        <WarningCircleIcon className="size-3.5" weight="fill" />
        Confirm to continue
      </div>
      <p className="text-foreground text-sm font-medium">
        {confirmation.title}
      </p>
      {confirmation.detail ? (
        <p className="text-muted-foreground text-xs">{confirmation.detail}</p>
      ) : null}
      {confirmation.consequences && confirmation.consequences.length > 0 ? (
        <ul className="text-muted-foreground flex flex-col gap-1 text-xs">
          {confirmation.consequences.map((line, i) => (
            <li key={i} className="flex items-start gap-1.5">
              <span className="bg-muted-foreground/50 mt-1.5 size-1 shrink-0 rounded-full" />
              {line}
            </li>
          ))}
        </ul>
      ) : null}
      <div className="mt-1 flex gap-2">
        <Button
          type="button"
          size="sm"
          onClick={() => api.resolveConfirmation(true)}
        >
          {confirmation.confirmLabel ?? "Confirm"}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => api.resolveConfirmation(false)}
        >
          {confirmation.cancelLabel ?? "Not yet"}
        </Button>
      </div>
    </div>
  );
}

const ACTIVITY_LABEL: Record<string, string> = {
  idle: "Ready",
  thinking: "Working",
  waiting: "Needs your input",
};

function WizardAssist({
  defaultOpen = true,
  className,
  ...props
}: React.ComponentProps<"aside"> & { defaultOpen?: boolean }) {
  const api = useWizardContext();
  const [open, setOpen] = React.useState(defaultOpen);
  const [draft, setDraft] = React.useState("");
  const { agent } = api;

  if (!agent.enabled) return null;

  const pendingHere = agent.proposalsOf(api.activeStepId).length;

  if (!open) {
    return (
      <button
        type="button"
        data-slot="wizard-assist"
        data-state="collapsed"
        onClick={() => setOpen(true)}
        aria-label={`Open ${agent.name}`}
        className="bg-sidebar hover:bg-sidebar-accent flex w-11 shrink-0 flex-col items-center gap-2 border-l py-3 transition-colors"
      >
        <SparkleIcon className="text-agent size-5" weight="fill" />
        {agent.activity === "thinking" ? (
          <span className="bg-agent size-1.5 animate-pulse rounded-full" />
        ) : null}
        {pendingHere > 0 ? (
          <Badge className="bg-agent text-agent-foreground size-4 justify-center p-0 text-[10px]">
            {pendingHere}
          </Badge>
        ) : null}
      </button>
    );
  }

  return (
    <aside
      data-slot="wizard-assist"
      data-state="open"
      className={cn(
        "bg-sidebar flex w-80 shrink-0 flex-col border-l",
        className,
      )}
      {...props}
    >
      <ThinkingLine active={agent.activity === "thinking"} />
      <header className="flex items-center gap-2 px-3 py-2.5">
        <SparkleIcon className="text-agent size-4" weight="fill" />
        <span className="text-foreground text-sm font-medium">
          {agent.name}
        </span>
        <span className="text-muted-foreground text-xs">
          {ACTIVITY_LABEL[agent.activity]}
        </span>
        <div className="flex-1" />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-7"
          aria-label={`Collapse ${agent.name}`}
          onClick={() => setOpen(false)}
        >
          <XIcon />
        </Button>
      </header>

      <div className="flex flex-1 flex-col gap-3 overflow-y-auto px-3 pb-3">
        {agent.messages.length === 0 ? (
          <p className="text-muted-foreground px-1 py-2 text-xs">
            {agent.name} follows along as you work and offers suggestions at
            each step. Ask a question any time.
          </p>
        ) : null}
        {agent.messages.map((message) =>
          message.role === "agent" ? (
            <div key={message.id} className="flex gap-2">
              <SparkleIcon
                className="text-agent mt-0.5 size-3.5 shrink-0"
                weight="fill"
              />
              <p className="text-foreground/90 text-sm">{message.text}</p>
            </div>
          ) : (
            <p
              key={message.id}
              className="bg-background text-foreground self-end rounded-md border px-2.5 py-1.5 text-sm"
            >
              {message.text}
            </p>
          ),
        )}
      </div>

      <div className="flex flex-col gap-2 border-t p-3">
        {agent.autopilot.available ? (
          agent.autopilot.running ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={agent.pauseAutopilot}
            >
              <PauseIcon weight="fill" />
              Pause autopilot
            </Button>
          ) : (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={agent.startAutopilot}
            >
              <SparkleIcon weight="fill" />
              Run autopilot
            </Button>
          )
        ) : null}
        <form
          className="flex items-center gap-1.5"
          onSubmit={(event) => {
            event.preventDefault();
            agent.ask(draft);
            setDraft("");
          }}
        >
          <Input
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={`Ask ${agent.name}`}
            className="h-8"
          />
          <Button
            type="submit"
            variant="ghost"
            size="icon"
            className="size-8"
            aria-label="Send"
            disabled={!draft.trim()}
          >
            <PaperPlaneTiltIcon />
          </Button>
        </form>
      </div>
    </aside>
  );
}

export {
  Wizard,
  WizardRail,
  WizardSurface,
  WizardHeader,
  WizardPanel,
  WizardField,
  WizardConfirm,
  WizardFooter,
  WizardAssist,
  useWizardContext,
  useWizard,
};
